<template>
    <ValidationObserver v-slot="{ dirty }" ref="observerConvocation">
        <form class="grid grid-cols-2 gap-2 w-full">
            <app-input-text
                :label="$t('commons.name')"
                v-model="meeting.name"
                class="col-span-2"
                :required="showRequired"
                @blur="save"
                :disabled="isReadOnly"
            />
            <app-text-editor
                ref="descriptionInput"
                class="col-span-2"
                :label="$t('project.follow.meetings.description')"
                v-model="meeting.description"
                :showToolbar="true"
                end-point="documents?scope=meetingDescription"
                @blur="saveText()"
                :disabled="isReadOnly"
            ></app-text-editor>
            <app-bundle-picker
                class="col-span-2"
                v-model="meeting.authorBundle"
                @input="meetingAuthorChanged"
                :label="$t('meetings.author')"
                :options="bundleOptions"
            ></app-bundle-picker>
            <app-select
                :label="$t('commons.type')"
                :disabled="disableType"
                v-if="showType"
                v-model="meeting.type"
                class="col-span-2"
                :required="showRequired"
                @input="save"
            >
                <option id="MOE">MOE</option>
                <option id="MOA">MOA</option>
            </app-select>
            <app-date-input
                :label="$t('commons.date')"
                :disabled="isReadOnly"
                v-model="meeting.date"
                class="col-span-2 md:col-span-1"
                :required="showRequired"
                @input="meetingDateChanged"
            />
            <app-time-input
                :label="$t('project.follow.meetings.hour')"
                v-model="meeting.date"
                :disabled="isReadOnly"
                class="col-span-2 md:col-span-1"
                :required="showRequired"
                @blur="meetingHourChanged"
            />
            <app-input-text
                :label="$t('project.follow.meetings.location')"
                v-model="meeting.location"
                :disabled="isReadOnly"
                class="col-span-2"
                @blur="meetingLocationChanged"
            />
        </form>
        <app-save-on-leave :dirty="dirty" :saveFn="() => save()"></app-save-on-leave>
    </ValidationObserver>
</template>

<script>
import AppInputText from '../../components/appInputText/AppInputText';
import AppFieldset from '../../components/appFieldset/AppFieldset';
import AppButton from '../../components/appButton/AppButton';
import AppMultiPicker from '../../components/appMultiPicker/AppMultiPicker';
import { getMeeting, updateMeeting } from './meetings.service';
import AppQuickActions from '../../components/appQuickActions/AppQuickActions';
import AppBackButton from '../../components/appBackButton/AppBackButton';
import AppSaveOnLeave from '../../components/AppSaveOnLeave';
import AppTimeInput from '@/components/appTimeInput/AppTimeInput';
import AppBundle from '@/components/app-bundle/appBundle';
import AppFileLink from '@/components/appFileLink/AppFileLink';
import AppLabel from '@/components/appLabel/AppLabel';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getMapById, sortBy } from '@/services/sanitize.service';
import IconEmailDownloadOutline from '@/icons/IconEmailDownloadOutline';
import MeetingReportMailPopup from '@/features/meetings/MeetingReportMailPopup.vue';
import MeetingConvocationsMailPopup from '@/features/meetings/MeetingConvocationsMailPopup.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import { getProject } from '@/features/projects/projects.service';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import { getLoggedUser } from '@/features/tokens/token.service';
import AppTextEditor from '@/components/appTextEditor/AppTextEditor.vue';

export default {
    components: {
        AppTextEditor,
        AppBundlePicker,
        AppSelect,
        AppDateInput,
        MeetingConvocationsMailPopup,
        MeetingReportMailPopup,
        IconEmailDownloadOutline,
        AppLabel,
        AppFileLink,
        AppBundle,
        AppTimeInput,
        AppSaveOnLeave,
        AppBackButton,
        AppQuickActions,
        AppMultiPicker,
        AppButton,
        AppFieldset,
        AppInputText,
    },
    props: {
        meetingId: String,
        showType: { type: Boolean, default: true },
        showRequired: { type: Boolean, default: true },
    },
    watch: {
        meetingId() {
            this.init();
        },
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getMeeting(this.$route.params.projectId, this.meetingId || this.$route.params.meetingId),
                    getBundles(this.$route.params.projectId),
                    getProject(this.$route.params.projectId),
                ]).subscribe(([meeting, bundles, project]) => {
                    this.isMOA = project.me.allowedFeatures.includes('project_meetingReportsMOA');
                    this.isMOE = project.me.allowedFeatures.includes('project_meetingReportsMOE');
                    this.disableType = !(this.isMOA && this.isMOE);
                    this.isReadOnly =
                        (meeting.type === 'MOA' && !this.isMOA) || (meeting.type === 'MOE' && !this.isMOE);
                    const bundleMap = getMapById(bundles);

                    const token = getLoggedUser();
                    const isRoot = token && token.roles.includes('root');
                    this.bundleOptions = isRoot
                        ? bundles
                        : bundles.filter((bundle) => project.me.bundleIds.includes(bundle.id));
                    if (meeting) {
                        this.meeting = {
                            ...meeting,
                            authorBundle: bundleMap[meeting.authorBundleId],
                            convocations: sortBy(
                                meeting.convocations.map((convocation) => ({
                                    ...convocation,
                                    convocationDate: convocation.convocationDate
                                        ? new Date(convocation.convocationDate)
                                        : null,
                                    bundle: bundleMap[convocation.bundleId],
                                })),
                                'convocationDate',
                            ),
                        };
                    }
                }),
            ];
        },
        meetingDateChanged() {
            return updateMeeting(this.$route.params.projectId, {
                id: this.meeting.id,
                date: this.meeting.date,
                convocations: this.meeting.convocations.map((convocation) => {
                    if (!convocation.convocationDate) {
                        convocation.convocationDate = this.meeting.date;
                    }
                    const hours = convocation.convocationDate.getHours();
                    const minutes = convocation.convocationDate.getMinutes();
                    convocation.convocationDate = new Date(this.meeting.date);
                    convocation.convocationDate.setHours(hours);
                    convocation.convocationDate.setMinutes(minutes);
                    return convocation;
                }),
            });
        },
        meetingHourChanged() {
            this.meeting.convocations.map((convocation) => {
                convocation.convocationDate = this.meeting.date;
            });
            return updateMeeting(this.$route.params.projectId, {
                id: this.meeting.id,
                date: this.meeting.date,
                convocations: this.meeting.convocations.map((convocation) => {
                    convocation.convocationDate = this.meeting.date;
                    return convocation;
                }),
            });
        },
        meetingLocationChanged() {
            return updateMeeting(this.$route.params.projectId, {
                id: this.meeting.id,
                location: this.meeting.location,
            });
        },
        save() {
            return updateMeeting(this.$route.params.projectId, {
                id: this.meeting.id,
                name: this.meeting.name,
                description: this.meeting.description,
                type: this.meeting.type,
            });
        },
        meetingAuthorChanged() {
            return updateMeeting(this.$route.params.projectId, {
                id: this.meeting.id,
                authorBundleId: this.meeting.authorBundle ? this.meeting.authorBundle.id : null,
            });
        },
        saveText() {
            this.$nextTick(() => this.save());
        },
    },
    data() {
        return {
            subscriptions: [],
            bundleOptions: [],
            meeting: {
                name: '',
                type: '',
            },
            disableType: false,
            isMOA: false,
            isMOE: false,
            isReadOnly: true,
        };
    },
};
</script>
